import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Button,
  cn,
} from '@design-system';
import {
  AddPartnerDialog,
  EditPartnerDialog,
  LinkExistingPartnerDialog,
} from '@/features/add-partner';
import { EditCareAssumptionsModal } from '..';

import { Users, Settings2, ChevronDown, Link, UserPlus } from 'lucide-react';

import { useState } from 'react';
import { useSelector } from 'react-redux';

import { selectSession } from '../model/selectors';
import { isNullOrUndefined, SessionType } from '@shared';

type CustomizePlanButtonProps = {
  client: Client;
  className?: string;
  showStressTestBtn?: boolean;
  showLinkPartnerBtn?: boolean;
  useDefaultLayout?: boolean;
  useIconOnlyOnMobile?: boolean;
};

const BUTTON_BASE_CLASSES =
  'flex items-center gap-2 w-full text-foreground font-medium px-1.5 md:px-3 whitespace-nowrap';
const ICON_WRAPPER_CLASSES = 'flex w-5 justify-center shrink-0';
const BUTTON_TEXT_CLASSES = 'text-xs md:text-sm';

type ActionButtonProps = {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  show?: boolean;
  useIconOnlyOnMobile?: boolean;
  testId?: string;
};

function ActionButton({
  icon,
  label,
  onClick,
  show = true,
  useIconOnlyOnMobile = false,
  testId,
}: ActionButtonProps) {
  if (!show) return null;

  return (
    <Button
      variant="outline"
      className={cn(BUTTON_BASE_CLASSES, {
        'px-2 md:px-3': useIconOnlyOnMobile,
      })}
      onClick={onClick}
      data-testid={testId}
    >
      <div className={ICON_WRAPPER_CLASSES}>{icon}</div>
      <span
        className={cn(BUTTON_TEXT_CLASSES, {
          'hidden md:inline': useIconOnlyOnMobile,
        })}
      >
        {label}
      </span>
    </Button>
  );
}

type PartnerActionButtonProps = {
  show: boolean;
  useIconOnlyOnMobile: boolean;
  isAdvisor: boolean;
  hasPartner: boolean;
  onEditPartner: () => void;
  onLinkExisting: () => void;
  onAddNew: () => void;
  onClientLink: () => void;
};

function PartnerActionButton({
  show,
  useIconOnlyOnMobile,
  isAdvisor,
  hasPartner,
  onEditPartner,
  onLinkExisting,
  onAddNew,
  onClientLink,
}: PartnerActionButtonProps) {
  if (!show) return null;

  if (!isAdvisor) {
    return (
      <ActionButton
        icon={<Users className="size-4" />}
        label="Add partner"
        onClick={onClientLink}
        useIconOnlyOnMobile={useIconOnlyOnMobile}
      />
    );
  }

  if (hasPartner) {
    return (
      <ActionButton
        icon={<Users className="size-4" />}
        label="Edit partner"
        onClick={onEditPartner}
        useIconOnlyOnMobile={useIconOnlyOnMobile}
      />
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger className="group w-full focus-visible:outline-none">
        <Button
          variant="outline"
          className={cn('justify-center', BUTTON_BASE_CLASSES, {
            'px-2 md:px-3': useIconOnlyOnMobile,
          })}
        >
          <div className={ICON_WRAPPER_CLASSES}>
            <Users className="size-4" />
          </div>
          <span className={cn(BUTTON_TEXT_CLASSES, useIconOnlyOnMobile && 'hidden md:inline')}>
            Add partner
          </span>
          <ChevronDown className="shrink-0 transition-transform group-data-[state=open]:rotate-180" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-full">
        <DropdownMenuItem onClick={onLinkExisting}>
          <Link className="size-4" />
          Link Existing Client as Partner
        </DropdownMenuItem>
        <DropdownMenuItem onClick={onAddNew}>
          <UserPlus className="size-4" />
          Add new partner
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function CustomizePlanButtonGroup({
  className,
  client,
  showStressTestBtn = true,
  showLinkPartnerBtn = true,
  useDefaultLayout = true,
  useIconOnlyOnMobile = false,
}: CustomizePlanButtonProps) {
  const { mutableClientPartner } = client;
  const { sessionType } = useSelector(selectSession);
  const [openStressTestModal, setOpenStressTestModal] = useState(false);
  const [openLinkExistingModal, setOpenLinkExistingModal] = useState(false);
  const [openAddPartnerModal, setOpenAddPartnerModal] = useState(false);
  const [openEditPartnerModal, setOpenEditPartnerModal] = useState(false);

  const isAdvisor = sessionType === SessionType.advisor || sessionType === SessionType.wizard;
  const hasPartner = !isNullOrUndefined(mutableClientPartner?.clientId);
  const showLinkPartnerBtnOverride = showLinkPartnerBtn && (isAdvisor || !hasPartner);

  return (
    <>
      <div
        className={cn(
          'text-foreground',
          {
            'grid grid-cols-2 gap-2 lg:absolute lg:mb-2 lg:ml-auto lg:mt-2 lg:grid-cols-1 lg:self-end':
              useDefaultLayout,
          },
          className,
        )}
      >
        <ActionButton
          icon={<Settings2 className="size-4" />}
          label="Edit care assumptions"
          onClick={() => setOpenStressTestModal(true)}
          show={showStressTestBtn}
          testId="edit-care-assumptions-button"
          useIconOnlyOnMobile={useIconOnlyOnMobile}
        />
        <PartnerActionButton
          show={showLinkPartnerBtnOverride}
          useIconOnlyOnMobile={useIconOnlyOnMobile}
          isAdvisor={isAdvisor}
          hasPartner={hasPartner}
          onEditPartner={() => setOpenEditPartnerModal(true)}
          onLinkExisting={() => setOpenLinkExistingModal(true)}
          onAddNew={() => setOpenAddPartnerModal(true)}
          onClientLink={() => setOpenAddPartnerModal(true)}
        />
      </div>

      <EditCareAssumptionsModal
        open={openStressTestModal}
        setOpen={setOpenStressTestModal}
      />

      {isAdvisor && (
        <LinkExistingPartnerDialog
          open={openLinkExistingModal}
          client={client}
          onClose={() => setOpenLinkExistingModal(false)}
        />
      )}

      <AddPartnerDialog
        open={openAddPartnerModal}
        onOpenChange={isOpen => {
          if (!isOpen) setOpenAddPartnerModal(false);
        }}
        clientId={client.clientId}
      />

      {mutableClientPartner && (
        <EditPartnerDialog
          open={openEditPartnerModal}
          onOpenChange={isOpen => {
            if (!isOpen) setOpenEditPartnerModal(false);
          }}
          client={client}
          partner={mutableClientPartner}
        />
      )}
    </>
  );
}
